import React, { useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { clearCommunicationHost, OverlayPortalContext, setCommunicationHost } from "../../lib/overlayPortal";

/**
 * 全站沟通入口：业务弹窗、PDF 全屏打开后仍可查看并操作聊天。
 * 单纯增大 z-index 无法越过浏览器全屏层，也不能解除弹窗的焦点限制。
 * 用手动 popover 提升到浏览器顶层，再把稳定的宿主放入当前业务弹窗的 DOM 范围；
 * 保留原弹窗的焦点保护，聊天输入仍可聚焦，移动宿主不重挂载 React/通话或消息草稿。
 * 不支持 Popover API 的旧浏览器退回 body 高层级，避免祖先 transform 裁切定位。
 */
export default function CommunicationLayer({ children }) {
  const [host] = useState(() => {
    const element = document.createElement("div");
    element.className = "communication-layer";
    element.setAttribute("data-communication-layer", "");
    if (typeof element.showPopover === "function") element.setAttribute("popover", "manual");
    return element;
  });

  useLayoutEffect(() => {
    let frame;
    const hasPopover = typeof host.showPopover === "function";
    const show = () => {
      if (hasPopover && host.isConnected && !host.matches(":popover-open")) host.showPopover();
    };
    const sync = () => {
      frame = null;
      // 只跟随业务弹窗，不跟随聊天自己的二级弹窗，防止将宿主移进自己的后代。
      const dialogs = [...document.querySelectorAll('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], dialog[open]')]
        .filter(el => !host.contains(el) && el.getClientRects().length);
      const top = dialogs.reduce((last, el) => !last || (Number(getComputedStyle(el).zIndex) || 0) >= (Number(getComputedStyle(last).zIndex) || 0) ? el : last, null);
      // 聊天内的视频也可能全屏；宿主不能移入自己的后代，否则会形成 DOM 循环。
      const fullscreen = document.fullscreenElement;
      const parent = (fullscreen && !host.contains(fullscreen) ? fullscreen : null) || (hasPopover && top) || document.body;
      if (host.parentElement !== parent) {
        const focused = host.contains(document.activeElement) ? document.activeElement : null;
        parent.appendChild(host);
        // Radix 在移动前可能把宿主列入背景隐藏范围；只恢复沟通宿主，不释放其他背景区域。
        host.removeAttribute("aria-hidden");
        show();
        focused?.focus({ preventScroll: true });
      } else show();
    };
    const schedule = () => { if (frame == null) frame = requestAnimationFrame(sync); };
    const observer = new MutationObserver(records => {
      if (records.some(record => !host.contains(record.target))) schedule();
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-state", "open"] });
    const onFullscreen = () => {
      // 外部 PDF 全屏后重新置顶；聊天内部视频全屏时不能隐藏其祖先，避免中断播放。
      if (hasPopover && !host.contains(document.fullscreenElement) && host.matches(":popover-open")) host.hidePopover();
      schedule();
    };
    document.addEventListener("fullscreenchange", onFullscreen);
    // 新原生顶层弹窗出现后重新置顶；manual 不会因点击其他页面或 Escape 而自行消失。
    const onToggle = (event) => {
      if (event.target !== host && !host.contains(event.target) && event.newState === "open") {
        if (hasPopover && host.matches(":popover-open")) host.hidePopover();
        schedule();
      }
    };
    document.addEventListener("toggle", onToggle, true);
    sync();
    setCommunicationHost(host);
    return () => {
      observer.disconnect();
      if (frame != null) cancelAnimationFrame(frame);
      document.removeEventListener("fullscreenchange", onFullscreen);
      document.removeEventListener("toggle", onToggle, true);
      clearCommunicationHost(host);
      host.remove();
    };
  }, [host]);

  return createPortal(<OverlayPortalContext.Provider value={host}>{children}</OverlayPortalContext.Provider>, host);
}
